import * as React from 'react';
import { createContext, useContext, useState, useCallback, useRef } from 'react';
import Modal from '../components/Modal';
import { personalakteStyles as ps, colors } from '../features/employees/styles/personalakteStyles';
import type { ModalOptions, ModalAction } from '../shared/utils/dialog';

interface ConfirmOptions {
  title: string;
  message: string;
  confirmLabel?: string;
  cancelLabel?: string;
  variant?: 'default' | 'warning' | 'error';
}

interface AlertOptions {
  title: string;
  message: string;
  variant?: 'default' | 'warning' | 'error' | 'success';
}

interface DialogContextValue {
  confirm: (options: ConfirmOptions) => Promise<boolean>;
  alert: (options: AlertOptions) => Promise<void>;
  showModal: (options: ModalOptions) => void;
}

const DialogContext = createContext<DialogContextValue | null>(null);

export function useDialog(): DialogContextValue {
  const ctx = useContext(DialogContext);
  if (!ctx) {
    throw new Error('useDialog must be used within a DialogProvider');
  }
  return ctx;
}

// Allow external code (dialog.ts singleton) to call into the provider
let globalDialogRef: DialogContextValue | null = null;

export function getGlobalDialog(): DialogContextValue | null {
  return globalDialogRef;
}

interface DialogState {
  type: 'confirm' | 'alert' | 'modal';
  options: any;
  resolve?: (value: any) => void;
}

export function DialogProvider({ children }: { children: React.ReactNode }): React.JSX.Element {
  const [dialog, setDialog] = useState<DialogState | null>(null);
  const resolveRef = useRef<((value: any) => void) | null>(null);

  const closeDialog = useCallback((result?: any) => {
    if (resolveRef.current) {
      resolveRef.current(result);
      resolveRef.current = null;
    }
    setDialog(null);
  }, []);

  const confirm = useCallback((options: ConfirmOptions): Promise<boolean> => {
    return new Promise(resolve => {
      resolveRef.current = resolve;
      setDialog({ type: 'confirm', options });
    });
  }, []);

  const alert = useCallback((options: AlertOptions): Promise<void> => {
    return new Promise(resolve => {
      resolveRef.current = resolve;
      setDialog({ type: 'alert', options });
    });
  }, []);

  const showModal = useCallback((options: ModalOptions) => {
    setDialog({ type: 'modal', options });
  }, []);

  const value: DialogContextValue = { confirm, alert, showModal };

  // Register globally so dialog.ts can use it
  React.useEffect(() => {
    globalDialogRef = value;
    return () => {
      globalDialogRef = null;
    };
  });

  const renderConfirm = () => {
    if (!dialog || dialog.type !== 'confirm') return null;
    const opts = dialog.options as ConfirmOptions;
    return (
      <Modal
        isOpen
        onClose={() => closeDialog(false)}
        title={opts.title}
        size="sm"
        variant={opts.variant || 'default'}
        footer={
          <>
            <button
              className="hover-btn-secondary"
              onClick={() => closeDialog(false)}
              style={ps.buttonSecondary}
              data-testid="dialog-cancel-button"
            >
              {opts.cancelLabel || 'Cancel'}
            </button>
            <button
              className="hover-btn-primary"
              onClick={() => closeDialog(true)}
              style={{
                ...ps.buttonPrimary,
                ...(opts.variant === 'error'
                  ? {
                      background: colors.danger,
                      boxShadow: '0 2px 8px rgba(239, 68, 68, 0.25)',
                    }
                  : {}),
              }}
              autoFocus
              data-testid="dialog-confirm-button"
            >
              {opts.confirmLabel || 'OK'}
            </button>
          </>
        }
      >
        <p style={{ margin: 0, fontSize: 13, color: colors.textBody, lineHeight: 1.6 }}>
          {opts.message}
        </p>
      </Modal>
    );
  };

  const renderAlert = () => {
    if (!dialog || dialog.type !== 'alert') return null;
    const opts = dialog.options as AlertOptions;
    return (
      <Modal
        isOpen
        onClose={() => closeDialog()}
        title={opts.title}
        size="sm"
        variant={opts.variant || 'default'}
        footer={
          <button
            className="hover-btn-primary"
            onClick={() => closeDialog()}
            style={ps.buttonPrimary}
            autoFocus
          >
            OK
          </button>
        }
      >
        <p style={{ margin: 0, fontSize: 13, color: colors.textBody, lineHeight: 1.6 }}>
          {opts.message}
        </p>
      </Modal>
    );
  };

  const renderModal = () => {
    if (!dialog || dialog.type !== 'modal') return null;
    const opts = dialog.options as ModalOptions;
    const contentText = typeof opts.content === 'string' ? opts.content : null;
    return (
      <Modal
        isOpen
        onClose={() => closeDialog()}
        title={opts.title}
        size="md"
        variant={opts.variant === 'info' ? 'default' : (opts.variant || 'default')}
        closeOnBackdrop={opts.closeOnBackdropPress !== false}
        footer={
          <>
            {opts.secondaryAction && (
              <button
                className="hover-btn-secondary"
                onClick={() => {
                  opts.secondaryAction!.onPress();
                  closeDialog();
                }}
                style={ps.buttonSecondary}
              >
                {opts.secondaryAction.label}
              </button>
            )}
            {opts.primaryAction && (
              <button
                className="hover-btn-primary"
                onClick={() => {
                  opts.primaryAction!.onPress();
                  closeDialog();
                }}
                style={{
                  ...ps.buttonPrimary,
                  ...(opts.primaryAction.variant === 'danger'
                    ? {
                        background: colors.danger,
                        boxShadow: '0 2px 8px rgba(239, 68, 68, 0.25)',
                      }
                    : {}),
                }}
              >
                {opts.primaryAction.label}
              </button>
            )}
          </>
        }
      >
        {contentText ? (
          <p style={{ margin: 0, fontSize: 13, color: colors.textBody, lineHeight: 1.6 }}>
            {contentText}
          </p>
        ) : (
          opts.content
        )}
      </Modal>
    );
  };

  return (
    <DialogContext.Provider value={value}>
      {children}
      {renderConfirm()}
      {renderAlert()}
      {renderModal()}
    </DialogContext.Provider>
  );
}
